import { normalizeLocale, type SupportedLocale } from './config';

type YourImaiVoiceCopy = {
  startCall: string;
  startingCall: string;
  activeCall: string;
  voiceMenu: string;
  alwaysShowAvatar: string;
  showAvatarDuringCalls: string;
  hideAvatar: string;
  startFailed: string;
  saveFailed: string;
};

const COPY: Record<SupportedLocale, YourImaiVoiceCopy> = {
  en: {
    startCall: 'Call Your IMAI',
    startingCall: 'Starting call…',
    activeCall: 'Your IMAI call in progress',
    voiceMenu: 'Voice options',
    alwaysShowAvatar: 'Always show avatar',
    showAvatarDuringCalls: 'Show only during calls',
    hideAvatar: 'Hide avatar',
    startFailed: 'Could not start the call. Please try again.',
    saveFailed: 'Could not save your avatar preference.',
  },
  ar: {
    startCall: 'اتصل بـ Your IMAI',
    startingCall: 'جارٍ بدء المكالمة…',
    activeCall: 'مكالمة Your IMAI جارية',
    voiceMenu: 'خيارات الصوت',
    alwaysShowAvatar: 'إظهار الشخصية دائمًا',
    showAvatarDuringCalls: 'الإظهار أثناء المكالمات فقط',
    hideAvatar: 'إخفاء الشخصية',
    startFailed: 'تعذر بدء المكالمة. حاول مرة أخرى.',
    saveFailed: 'تعذر حفظ تفضيل الشخصية.',
  },
  de: {
    startCall: 'Your IMAI anrufen',
    startingCall: 'Anruf wird gestartet…',
    activeCall: 'Your IMAI-Anruf läuft',
    voiceMenu: 'Sprachoptionen',
    alwaysShowAvatar: 'Avatar immer anzeigen',
    showAvatarDuringCalls: 'Nur bei Anrufen anzeigen',
    hideAvatar: 'Avatar ausblenden',
    startFailed: 'Anruf konnte nicht gestartet werden. Bitte erneut versuchen.',
    saveFailed: 'Avatar-Einstellung konnte nicht gespeichert werden.',
  },
  es: {
    startCall: 'Llamar a Your IMAI',
    startingCall: 'Iniciando llamada…',
    activeCall: 'Llamada con Your IMAI en curso',
    voiceMenu: 'Opciones de voz',
    alwaysShowAvatar: 'Mostrar siempre el avatar',
    showAvatarDuringCalls: 'Mostrar solo durante llamadas',
    hideAvatar: 'Ocultar avatar',
    startFailed: 'No se pudo iniciar la llamada. Inténtalo de nuevo.',
    saveFailed: 'No se pudo guardar la preferencia del avatar.',
  },
  fr: {
    startCall: 'Appeler Your IMAI',
    startingCall: 'Démarrage de l’appel…',
    activeCall: 'Appel Your IMAI en cours',
    voiceMenu: 'Options vocales',
    alwaysShowAvatar: 'Toujours afficher l’avatar',
    showAvatarDuringCalls: 'Afficher uniquement pendant les appels',
    hideAvatar: 'Masquer l’avatar',
    startFailed: 'Impossible de démarrer l’appel. Réessayez.',
    saveFailed: 'Impossible d’enregistrer la préférence de l’avatar.',
  },
  hi: {
    startCall: 'Your IMAI को कॉल करें',
    startingCall: 'कॉल शुरू हो रही है…',
    activeCall: 'Your IMAI कॉल जारी है',
    voiceMenu: 'वॉइस विकल्प',
    alwaysShowAvatar: 'अवतार हमेशा दिखाएँ',
    showAvatarDuringCalls: 'केवल कॉल के दौरान दिखाएँ',
    hideAvatar: 'अवतार छिपाएँ',
    startFailed: 'कॉल शुरू नहीं हो सकी। फिर कोशिश करें।',
    saveFailed: 'अवतार की पसंद सहेजी नहीं जा सकी।',
  },
  id: {
    startCall: 'Telepon Your IMAI',
    startingCall: 'Memulai panggilan…',
    activeCall: 'Panggilan Your IMAI berlangsung',
    voiceMenu: 'Opsi suara',
    alwaysShowAvatar: 'Selalu tampilkan avatar',
    showAvatarDuringCalls: 'Tampilkan hanya saat panggilan',
    hideAvatar: 'Sembunyikan avatar',
    startFailed: 'Tidak dapat memulai panggilan. Coba lagi.',
    saveFailed: 'Tidak dapat menyimpan preferensi avatar.',
  },
  it: {
    startCall: 'Chiama Your IMAI',
    startingCall: 'Avvio chiamata…',
    activeCall: 'Chiamata Your IMAI in corso',
    voiceMenu: 'Opzioni vocali',
    alwaysShowAvatar: 'Mostra sempre l’avatar',
    showAvatarDuringCalls: 'Mostra solo durante le chiamate',
    hideAvatar: 'Nascondi avatar',
    startFailed: 'Impossibile avviare la chiamata. Riprova.',
    saveFailed: 'Impossibile salvare la preferenza dell’avatar.',
  },
  ja: {
    startCall: 'Your IMAIに電話する',
    startingCall: '通話を開始中…',
    activeCall: 'Your IMAIと通話中',
    voiceMenu: '音声オプション',
    alwaysShowAvatar: 'アバターを常に表示',
    showAvatarDuringCalls: '通話中のみ表示',
    hideAvatar: 'アバターを非表示',
    startFailed: '通話を開始できませんでした。もう一度お試しください。',
    saveFailed: 'アバターの設定を保存できませんでした。',
  },
  ko: {
    startCall: 'Your IMAI에 전화하기',
    startingCall: '통화 시작 중…',
    activeCall: 'Your IMAI 통화 중',
    voiceMenu: '음성 옵션',
    alwaysShowAvatar: '항상 아바타 표시',
    showAvatarDuringCalls: '통화 중에만 표시',
    hideAvatar: '아바타 숨기기',
    startFailed: '통화를 시작할 수 없습니다. 다시 시도하세요.',
    saveFailed: '아바타 설정을 저장할 수 없습니다.',
  },
  'pt-BR': {
    startCall: 'Ligar para Your IMAI',
    startingCall: 'Iniciando chamada…',
    activeCall: 'Chamada com Your IMAI em andamento',
    voiceMenu: 'Opções de voz',
    alwaysShowAvatar: 'Sempre mostrar avatar',
    showAvatarDuringCalls: 'Mostrar apenas durante chamadas',
    hideAvatar: 'Ocultar avatar',
    startFailed: 'Não foi possível iniciar a chamada. Tente novamente.',
    saveFailed: 'Não foi possível salvar a preferência do avatar.',
  },
  ru: {
    startCall: 'Позвонить Your IMAI',
    startingCall: 'Начало вызова…',
    activeCall: 'Идёт вызов Your IMAI',
    voiceMenu: 'Настройки голоса',
    alwaysShowAvatar: 'Всегда показывать аватар',
    showAvatarDuringCalls: 'Показывать только во время звонков',
    hideAvatar: 'Скрыть аватар',
    startFailed: 'Не удалось начать вызов. Попробуйте ещё раз.',
    saveFailed: 'Не удалось сохранить настройку аватара.',
  },
  vi: {
    startCall: 'Gọi Your IMAI',
    startingCall: 'Đang bắt đầu cuộc gọi…',
    activeCall: 'Cuộc gọi Your IMAI đang diễn ra',
    voiceMenu: 'Tùy chọn giọng nói',
    alwaysShowAvatar: 'Luôn hiển thị avatar',
    showAvatarDuringCalls: 'Chỉ hiển thị trong cuộc gọi',
    hideAvatar: 'Ẩn avatar',
    startFailed: 'Không thể bắt đầu cuộc gọi. Vui lòng thử lại.',
    saveFailed: 'Không thể lưu tùy chọn avatar.',
  },
  zh: {
    startCall: '呼叫 Your IMAI',
    startingCall: '正在开始通话…',
    activeCall: '正在与 Your IMAI 通话',
    voiceMenu: '语音选项',
    alwaysShowAvatar: '始终显示头像',
    showAvatarDuringCalls: '仅在通话期间显示',
    hideAvatar: '隐藏头像',
    startFailed: '无法开始通话，请重试。',
    saveFailed: '无法保存头像偏好设置。',
  },
  'zh-TW': {
    startCall: '呼叫 Your IMAI',
    startingCall: '正在開始通話…',
    activeCall: '正在與 Your IMAI 通話',
    voiceMenu: '語音選項',
    alwaysShowAvatar: '一律顯示頭像',
    showAvatarDuringCalls: '僅在通話期間顯示',
    hideAvatar: '隱藏頭像',
    startFailed: '無法開始通話，請重試。',
    saveFailed: '無法儲存頭像偏好設定。',
  },
};

export function getYourImaiVoiceCopy(locale: string | null | undefined) {
  return COPY[normalizeLocale(locale)];
}
